import { configureStore, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux"
import { AvailableSizes, ColorSwatch, ProductState } from "./types"

// Define the initial state using that type
const initialState: ProductState = {
    colorSwatches: [],
    selectedColor: "",
    availableSizes: [],
    selectedSize: "",
    canAddToCart: false,
    addedProduct: {
        name: "",
        size: "",
        price: "",
        image: "",
        color_name: ""
    } // New state for added product
}

const productSlice = createSlice({
    name: "product",
    initialState,
    reducers: {
        setColorSwatches: (state, action: PayloadAction<ColorSwatch[]>) => {
            state.colorSwatches = action.payload.map(swatch => ({
                ...swatch,
                isSelected: swatch.isSelected ?? false
            }))

            const selectedSwatch = action.payload.find(swatch => swatch.isSelected)
            state.selectedColor = selectedSwatch ? selectedSwatch.color_name : ""
        },
        setAvailableSizes: (state, action: PayloadAction<AvailableSizes[]>) => {
            state.availableSizes = action.payload
            const selectedSize = action.payload.find(size => size.isSelected)

            state.selectedSize = selectedSize ? selectedSize.name : ""
        },

        selectSize: (state, action: PayloadAction<string>) => {
            state.availableSizes.forEach(size => {
                size.isSelected = size.name === action.payload
            })
            state.selectedSize = action.payload
        },

        selectColorSwatch: (state, action: PayloadAction<string>) => {
            state.colorSwatches.forEach(swatch => {
                swatch.isSelected = swatch.color_name === action.payload
            })
            state.selectedColor = action.payload
        },
        updateCanAddToCart: state => {
            const isColorSelectedAndAvailable = state.colorSwatches.some(
                swatch => swatch.isSelected && swatch.isAvailable
            )
            const isSizeSelectedAndAvailable = state.availableSizes.some(
                size => size.isSelected && size.isAvailable
            )

            state.canAddToCart = isColorSelectedAndAvailable && isSizeSelectedAndAvailable
        },
        setAddedProduct: (
            state,
            action: PayloadAction<{
                name: string
                size: string
                price: string
                image: string
                color_name: string
            }>
        ) => {
            state.addedProduct = action.payload // Update the added product state
        }
    }
})

export const {
    setColorSwatches,
    setAvailableSizes,
    selectColorSwatch,
    selectSize,
    updateCanAddToCart,
    setAddedProduct
} = productSlice.actions

export const makeStore = () =>
    configureStore({
        reducer: {
            product: productSlice.reducer
        }
    })

export type AppStore = ReturnType<typeof makeStore>
type AppState = ReturnType<AppStore["getState"]>
type AppDispatch = AppStore["dispatch"]

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<AppState> = useSelector
